import { getRouteWithQueryParams } from "../../../routes"
import { getData } from "../apiService"
import { getBaseEndpoint } from "../../endpoints/endpointManager"
import appInsightsLogger from "../../logging/appInsightsLogger"

const apiRoutes = {
  get: {
    GEOCODED_LOCATION: "https://atlas.microsoft.com/search/address/json",
  },
}

// Search service endpoints for Azure Maps
const searchServiceRoutes = {
  get: {
    GEOCODED_LOCATIONS: "/search/geocoding/search",
    GEOCODED_LOCATION: "/search/geocoding/location",
  },
}

/**
 * Get geocoded locations using search service (with Entra ID authentication)
 */
const getGeocodedLocationsByAddressViaSearchService = async (
  address: string,
): Promise<GeocodedLocation[]> => {
  const baseEndpoint = getBaseEndpoint()
  const queryParams = {
    address: address,
    language: "en-US",
  }

  const urlWithParams = getRouteWithQueryParams(
    `${baseEndpoint}${searchServiceRoutes.get.GEOCODED_LOCATIONS}`,
    queryParams,
  )

  const response = await getData<{ results: GeocodedLocation[] }>(urlWithParams)
  return response.results
}

/**
 * Get best geocoded location using search service (with Entra ID authentication)
 */
const getGeocodedLocationViaSearchService = async (
  address: string,
): Promise<GeocodedLocation> => {
  const baseEndpoint = getBaseEndpoint()
  const queryParams = {
    address: address,
    language: "en-US",
  }

  const urlWithParams = getRouteWithQueryParams(
    `${baseEndpoint}${searchServiceRoutes.get.GEOCODED_LOCATION}`,
    queryParams,
  )

  const response = await getData<{ location?: GeocodedLocation }>(urlWithParams)
  if (!response?.location) {
    throw new Error("Search service geocoding response missing location")
  }
  return response.location
}

/**
 * Get geocoded locations using direct Azure Maps API (subscription key)
 */
const getGeocodedLocationsByAddressLegacy = async (
  address: string,
): Promise<GeocodedLocation[]> => {
  const subscriptionKey = window._env_.AZURE_MAPS_SUBSCRIPTION_KEY
  if (!subscriptionKey) {
    throw new Error("Missing Azure Maps subscription key")
  }
  const queryParams = {
    "subscription-key": subscriptionKey,
    "api-version": "1.0",
    language: "en-US",
    query: address,
  }
  const urlWithParams = getRouteWithQueryParams(
    apiRoutes.get.GEOCODED_LOCATION,
    queryParams,
  )
  const geocodedLocationResponse = await getData<{
    results: GeocodedLocation[]
  }>(urlWithParams)
  return geocodedLocationResponse.results
}

/**
 * Get best geocoded location using direct Azure Maps API (subscription key)
 */
const getGeocodedLocationLegacy = async (
  address: string,
): Promise<GeocodedLocation> => {
  const subscriptionKey = window._env_.AZURE_MAPS_SUBSCRIPTION_KEY
  if (!subscriptionKey) {
    throw new Error("Missing Azure Maps subscription key")
  }
  const queryParams = {
    "subscription-key": subscriptionKey,
    "api-version": "1.0",
    language: "en-US",
    query: address,
  }
  const urlWithParams = getRouteWithQueryParams(
    apiRoutes.get.GEOCODED_LOCATION,
    queryParams,
  )
  const geocodedLocationResponse = await getData<{
    results: GeocodedLocation[]
  }>(urlWithParams)
  const bestMatch = geocodedLocationResponse.results[0] ?? undefined
  if (!bestMatch) {
    throw new Error("Unable to retrieve geocoded location")
  }
  return bestMatch
}

const getGeocodedLocationsByAddress = async (
  address: string,
  useSearchService: boolean,
) => {
  if (useSearchService) {
    appInsightsLogger.traceInfo(
      "Using search service geocoding with Entra ID authentication",
    )
    try {
      return await getGeocodedLocationsByAddressViaSearchService(address)
    } catch (error) {
      appInsightsLogger.traceWarning(
        "Search service geocoding failed, falling back to Azure Maps subscription key geocoding",
        error,
      )
      return await getGeocodedLocationsByAddressLegacy(address)
    }
  } else {
    appInsightsLogger.traceInfo("Using Azure Maps API with subscription key")
    return await getGeocodedLocationsByAddressLegacy(address)
  }
}

const getGeocodedLocation = async (
  address: string,
  useSearchService: boolean,
) => {
  if (useSearchService) {
    appInsightsLogger.traceInfo(
      "Using search service geocoding with Entra ID authentication",
    )
    try {
      return await getGeocodedLocationViaSearchService(address)
    } catch (error) {
      appInsightsLogger.traceWarning(
        "Search service geocoding failed, falling back to Azure Maps subscription key geocoding",
        error,
      )
      return await getGeocodedLocationLegacy(address)
    }
  } else {
    appInsightsLogger.traceInfo("Using Azure Maps API with subscription key")
    return await getGeocodedLocationLegacy(address)
  }
}

/**
 * Get Azure Maps access token via search service (with Entra ID authentication)
 */
const getAzureMapsAccessToken = async () => {
  appInsightsLogger.traceInfo(
    "Using search service Azure Maps authentication with Entra ID",
  )
  const baseEndpoint = getBaseEndpoint()
  const url = `${baseEndpoint}/search/maps/token`

  const response = await getData<{
    accessToken: string
    expiresOn: string | null
  }>(url)

  return response
}

export const externalApi = {
  get: {
    GEOCODED_LOCATIONS_BY_ADDRESS: getGeocodedLocationsByAddress,
    GEOCODED_LOCATION: getGeocodedLocation,
    AZURE_MAPS_ACCESS_TOKEN: getAzureMapsAccessToken,
  },
}
